<template>
  <div class="help-page">
    <div class="help-container">
      <div class="help-header">
        <h1>
          用户操作指南
        </h1>
        <p class="subtitle">智能记单词，高效不遗忘</p>
      </div>

      <el-tabs v-model="activeTab" type="border-card" class="help-tabs">
        <!-- 快速开始 -->
        <el-tab-pane label="快速开始" name="quickstart">
          <div class="tab-content">
            <h2>🚀 快速开始</h2>
            
            <el-card class="guide-card">
              <h3>1️⃣ 登录系统</h3>
              <ol>
                <li>打开应用，进入登录页面</li>
                <li>输入用户名和密码</li>
                <li>可勾选"记住我"保持登录状态</li>
                <li>点击"登录"按钮</li>
              </ol>
              <el-alert type="info" :closable="false">
                <strong>快捷键：</strong>输入密码后按 Enter 键快速登录
              </el-alert>
            </el-card>

            <el-card class="guide-card">
              <h3>2️⃣ 开始学习</h3>
              <ol>
                <li>在首页左侧选择学习阶段</li>
                <li>点击单元的"开始学习"按钮</li>
                <li>按照三阶段学习法完成学习</li>
                <li>查看学习总结和获得奖励</li>
              </ol>
            </el-card>

            <el-card class="guide-card">
              <h3>3️⃣ 定期复习</h3>
              <ol>
                <li>系统会自动计算复习时间</li>
                <li>首页显示待复习单词提醒</li>
                <li>点击"开始复习"进入复习流程</li>
                <li>按时复习巩固记忆</li>
              </ol>
            </el-card>
          </div>
        </el-tab-pane>

        <!-- 主要功能 -->
        <el-tab-pane label="主要功能" name="features">
          <div class="tab-content">
            <h2>📚 主要功能</h2>
            
            <el-collapse accordion>
              <el-collapse-item title="🏠 学习中心（首页）" name="1">
                <h4>今日学习概览</h4>
                <ul>
                  <li>复习单词：今天已复习的单词数量</li>
                  <li>新学单词：今天学习的新单词数量</li>
                  <li>学习时长：累计学习时间</li>
                  <li>获得金币：今日奖励金币数</li>
                </ul>
                
                <h4>课程单元列表</h4>
                <ul>
                  <li>查看当前阶段所有学习单元</li>
                  <li>显示单元名称、单词数量、学习进度</li>
                  <li>点击"开始学习"进入三阶段学习</li>
                </ul>
                
                <h4>阶段排行榜</h4>
                <ul>
                  <li>查看当前阶段的完成排名</li>
                  <li>显示个人排名和学习数据</li>
                  <li>排名指标：完成时长、学习天数、正确率</li>
                </ul>
              </el-collapse-item>

              <el-collapse-item title="📖 单词库管理" name="2">
                <h4>功能特点</h4>
                <ul>
                  <li>支持按单词、中文释义搜索</li>
                  <li>显示音标、释义、例句</li>
                  <li>标注学习阶段和掌握状态</li>
                  <li>一键播放标准发音</li>
                  <li>查看复习次数和正确率</li>
                </ul>
              </el-collapse-item>

              <el-collapse-item title="✅ 自测系统" name="3">
                <h4>测试类型</h4>
                <ul>
                  <li>看英文选中文</li>
                  <li>看中文选英文</li>
                  <li>拼写测试</li>
                </ul>
                <h4>功能特点</h4>
                <ul>
                  <li>即时答题反馈</li>
                  <li>错误显示正确答案</li>
                  <li>统计正确率和用时</li>
                  <li>保存历史测试记录</li>
                </ul>
              </el-collapse-item>

              <el-collapse-item title="📊 学习报告" name="4">
                <h4>统计数据</h4>
                <ul>
                  <li>累计学习天数和时长</li>
                  <li>学习单词总数和掌握数</li>
                  <li>学习时长趋势图</li>
                  <li>单词掌握趋势分析</li>
                  <li>正确率统计图表</li>
                  <li>各阶段单词分布</li>
                </ul>
                <h4>成就系统</h4>
                <ul>
                  <li>等级：初学者 → 传奇（10级）</li>
                  <li>连续学习天数记录</li>
                  <li>金币累计统计</li>
                  <li>学习里程碑徽章</li>
                </ul>
              </el-collapse-item>

              <el-collapse-item title="📈 记忆曲线" name="5">
                <h4>艾宾浩斯遗忘曲线</h4>
                <ul>
                  <li>第1阶段：10分钟后复习</li>
                  <li>第2阶段：1天后复习</li>
                  <li>第3阶段：3天后复习</li>
                  <li>第4阶段：7天后复习</li>
                  <li>第5阶段：15天后复习</li>
                </ul>
                <h4>个人曲线</h4>
                <ul>
                  <li>可视化展示您的复习数据</li>
                  <li>对比标准遗忘曲线</li>
                  <li>分析记忆效果</li>
                  <li>待复习单词列表（按优先级排序）</li>
                </ul>
              </el-collapse-item>

              <el-collapse-item title="⚙️ 个人设置" name="6">
                <h4>个人资料</h4>
                <ul>
                  <li>查看用户名、等级、经验值</li>
                  <li>累计金币和学习天数</li>
                  <li>修改头像和昵称</li>
                </ul>
                <h4>学习设置</h4>
                <ul>
                  <li>设置每日学习单词数</li>
                  <li>设置复习提醒时间</li>
                  <li>选择英音/美音</li>
                  <li>开启/关闭背景音乐</li>
                </ul>
              </el-collapse-item>
            </el-collapse>
          </div>
        </el-tab-pane>

        <!-- 学习流程 -->
        <el-tab-pane label="学习流程" name="learning">
          <div class="tab-content">
            <h2>🎓 完整学习流程</h2>
            
            <el-steps direction="vertical" :active="5">
              <el-step title="选择学习阶段" icon="el-icon-s-grid">
                <template slot="description">
                  <p>在首页左侧选择学习阶段（初级/中级/高级等）</p>
                  <p>查看阶段总体进度和单元列表</p>
                </template>
              </el-step>

              <el-step title="开始单元学习" icon="el-icon-video-play">
                <template slot="description">
                  <p>点击某个单元的"开始学习"按钮</p>
                  <p>进入三阶段学习对话框</p>
                </template>
              </el-step>

              <el-step title="第一阶段：英文→中文" icon="el-icon-view">
                <template slot="description">
                  <h4>目标：看英文单词，回忆中文意思</h4>
                  <ol>
                    <li>系统展示英文单词 + 音标</li>
                    <li>自动播放发音（2遍）</li>
                    <li>思考单词含义</li>
                    <li>点击"记得"或"不记得"按钮</li>
                  </ol>
                  <el-tag type="success" size="small">记得：2秒后进入下一个</el-tag>
                  <el-tag type="warning" size="small">不记得：显示答案，3秒后进入下一个</el-tag>
                </template>
              </el-step>

              <el-step title="第二阶段：中文→英文" icon="el-icon-refresh">
                <template slot="description">
                  <h4>目标：看中文释义，回忆英文单词</h4>
                  <ol>
                    <li>系统展示中文释义</li>
                    <li>思考对应的英文单词</li>
                    <li>点击"记得"或"不记得"</li>
                    <li>查看答案确认</li>
                  </ol>
                </template>
              </el-step>

              <el-step title="第三阶段：拼写测试" icon="el-icon-edit">
                <template slot="description">
                  <h4>目标：根据中文释义，正确拼写单词</h4>
                  <ol>
                    <li>系统展示中文释义 + 音标</li>
                    <li>在输入框输入英文单词</li>
                    <li>按 Enter 键或点击"检查"按钮</li>
                    <li>系统判断拼写正误</li>
                  </ol>
                  <el-alert type="success" :closable="false">
                    <strong>正确：</strong>绿色提示，3秒后进入下一个
                  </el-alert>
                  <el-alert type="error" :closable="false">
                    <strong>错误：</strong>红色提示显示正确答案，5秒后清空重新输入
                  </el-alert>
                  <el-alert type="info" :closable="false">
                    <strong>快捷键：</strong>Enter 键提交答案（支持平板软键盘）
                  </el-alert>
                </template>
              </el-step>

              <el-step title="查看学习总结" icon="el-icon-data-analysis">
                <template slot="description">
                  <p>完成所有单词后，显示学习成果：</p>
                  <ul>
                    <li>单词总数</li>
                    <li>认识/不认识单词数</li>
                    <li>掌握率百分比</li>
                    <li>学习用时和获得金币</li>
                  </ul>
                </template>
              </el-step>
            </el-steps>

            <el-divider></el-divider>

            <h3>🔄 复习机制</h3>
            <el-card class="guide-card">
              <h4>复习时间节点</h4>
              <el-timeline>
                <el-timeline-item timestamp="Stage 1" color="#67C23A">10分钟后复习</el-timeline-item>
                <el-timeline-item timestamp="Stage 2" color="#409EFF">1天后复习</el-timeline-item>
                <el-timeline-item timestamp="Stage 3" color="#E6A23C">3天后复习</el-timeline-item>
                <el-timeline-item timestamp="Stage 4" color="#F56C6C">7天后复习</el-timeline-item>
                <el-timeline-item timestamp="Stage 5" color="#909399">15天后复习</el-timeline-item>
              </el-timeline>

              <h4>复习结果影响</h4>
              <ul>
                <li><el-tag type="success" size="small">记得/拼写正确</el-tag> → 进入下一阶段</li>
                <li><el-tag type="danger" size="small">不记得/拼写错误</el-tag> → 重置到第1阶段</li>
                <li><el-tag type="info" size="small">第5阶段仍记得</el-tag> → 标记为"已掌握"</li>
              </ul>

              <h4>优先级算法</h4>
              <p>待复习单词按以下规则排序：</p>
              <ul>
                <li>过期时间（权重60%）：超期越久越优先</li>
                <li>记忆阶段（权重30%）：阶段越低越优先</li>
                <li>准确率（权重10%）：错误率越高越优先</li>
              </ul>
            </el-card>
          </div>
        </el-tab-pane>

        <!-- 常见问题 -->
        <el-tab-pane label="常见问题" name="faq">
          <div class="tab-content">
            <h2>❓ 常见问题</h2>
            
            <h3>学习相关</h3>
            <el-collapse accordion>
              <el-collapse-item title="Q1：每天应该学习多少单词？" name="q1">
                <ul>
                  <li>建议：新手 10-20 个/天，有基础 30-50 个/天</li>
                  <li>可在"个人设置"中调整每日目标</li>
                  <li>重质量不重数量，确保每个单词真正掌握</li>
                </ul>
              </el-collapse-item>

              <el-collapse-item title="Q2：为什么有些单词需要反复复习？" name="q2">
                <ul>
                  <li>这是艾宾浩斯遗忘曲线的科学规律</li>
                  <li>多次复习才能转化为长期记忆</li>
                  <li>建议按时完成系统提醒的复习</li>
                  <li>5个阶段完成后即可永久掌握</li>
                </ul>
              </el-collapse-item>

              <el-collapse-item title="Q3：拼写测试时能查看提示吗？" name="q3">
                <ul>
                  <li>可以点击"发音"按钮听单词读音</li>
                  <li>音标已显示在页面上</li>
                  <li>不建议直接查看答案，影响学习效果</li>
                  <li>如多次拼写错误，系统会显示正确答案</li>
                </ul>
              </el-collapse-item>

              <el-collapse-item title="Q4：如何快速提升掌握率？" name="q4">
                <ul>
                  <li>按时完成每日复习</li>
                  <li>重点关注"不记得"的单词</li>
                  <li>利用碎片时间多次复习</li>
                  <li>结合例句理解单词用法</li>
                  <li>朗读和听力结合，培养语感</li>
                </ul>
              </el-collapse-item>
            </el-collapse>

            <h3>操作相关</h3>
            <el-collapse accordion>
              <el-collapse-item title="Q5：平板上怎么使用？" name="q5">
                <ul>
                  <li>界面已适配平板</li>
                  <li>宽度小于600px时，顶部菜单只显示图标</li>
                  <li>支持触摸操作和软键盘输入</li>
                  <li>横屏/竖屏自动调整布局</li>
                  <li>建议竖屏浏览列表，横屏学习单词</li>
                </ul>
              </el-collapse-item>

              <el-collapse-item title="Q6：拼写输入框无法输入怎么办？" name="q6">
                <ul>
                  <li>检查是否已点击"显示答案"（会禁用输入）</li>
                  <li>刷新页面重新进入</li>
                  <li>确认输入法为英文模式</li>
                  <li>清除浏览器缓存后重试</li>
                </ul>
              </el-collapse-item>

              <el-collapse-item title="Q7：背景音乐在哪里开启？" name="q7">
                <ul>
                  <li>学习对话框右上角有"背景音乐"按钮</li>
                  <li>点击切换播放/暂停</li>
                  <li>可在个人设置中永久关闭</li>
                  <li>建议在安静环境学习时开启</li>
                </ul>
              </el-collapse-item>

              <el-collapse-item title="Q8：如何查看排行榜？" name="q8">
                <ul>
                  <li>首页下方"阶段排行榜"板块</li>
                  <li>选择不同阶段查看对应排名</li>
                  <li>显示个人排名、完成时长、正确率等</li>
                  <li>金色徽章标识优秀学员</li>
                </ul>
              </el-collapse-item>
            </el-collapse>

            <h3>数据相关</h3>
            <el-collapse accordion>
              <el-collapse-item title="Q9：学习数据会丢失吗？" name="q9">
                <ul>
                  <li>数据实时保存到服务器</li>
                  <li>更换设备登录后数据同步</li>
                  <li>建议定期登录保持会话</li>
                  <li>重要数据会定期备份</li>
                </ul>
              </el-collapse-item>

              <el-collapse-item title="Q10：金币有什么用？" name="q10">
                <ul>
                  <li>未来版本将开放金币商城</li>
                  <li>可兑换学习资源、徽章、主题等</li>
                  <li>当前可作为学习成就激励</li>
                  <li>学习、复习、测试都可获得金币</li>
                </ul>
              </el-collapse-item>

              <el-collapse-item title="Q11：等级如何提升？" name="q11">
                <ul>
                  <li>学习单词累计经验值</li>
                  <li>完成复习获得额外奖励</li>
                  <li>连续学习有经验加成</li>
                  <li>等级越高，头衔越高级（初学者→传奇）</li>
                  <li>10个等级对应不同成就</li>
                </ul>
              </el-collapse-item>
            </el-collapse>
          </div>
        </el-tab-pane>

        <!-- 学习建议 -->
        <el-tab-pane label="学习建议" name="tips">
          <div class="tab-content">
            <h2>💡 学习建议</h2>
            
            <el-card class="guide-card">
              <h3>🌟 高效学习技巧</h3>
              <el-row :gutter="20">
                <el-col :span="12">
                  <div class="tip-item">
                    <i class="el-icon-timer"></i>
                    <h4>每日固定时间</h4>
                    <p>建立学习习惯，效果更佳</p>
                  </div>
                </el-col>
                <el-col :span="12">
                  <div class="tip-item">
                    <i class="el-icon-refresh"></i>
                    <h4>少量多次</h4>
                    <p>分批学习比一次学完效果好</p>
                  </div>
                </el-col>
                <el-col :span="12">
                  <div class="tip-item">
                    <i class="el-icon-alarm-clock"></i>
                    <h4>及时复习</h4>
                    <p>收到复习提醒尽快完成</p>
                  </div>
                </el-col>
                <el-col :span="12">
                  <div class="tip-item">
                    <i class="el-icon-reading"></i>
                    <h4>结合例句</h4>
                    <p>理解单词用法，不死记硬背</p>
                  </div>
                </el-col>
                <el-col :span="12">
                  <div class="tip-item">
                    <i class="el-icon-headset"></i>
                    <h4>发音跟读</h4>
                    <p>多听多读，培养语感</p>
                  </div>
                </el-col>
                <el-col :span="12">
                  <div class="tip-item">
                    <i class="el-icon-notebook-2"></i>
                    <h4>错题重点</h4>
                    <p>关注"不记得"的单词，重点突破</p>
                  </div>
                </el-col>
              </el-row>
            </el-card>

            <el-card class="guide-card">
              <h3>📱 移动端使用技巧</h3>
              <ul>
                <li>竖屏适合浏览单词列表</li>
                <li>横屏适合学习对话框（图文并茂）</li>
                <li>碎片时间快速复习 5-10 个单词</li>
                <li>利用通勤时间听单词发音</li>
                <li>支持手势操作，快速切换</li>
              </ul>
            </el-card>

            <el-card class="guide-card">
              <h3>🎯 目标设定建议</h3>
              <el-table :data="goalTable" border style="width: 100%">
                <el-table-column prop="stage" label="学习阶段" width="120"></el-table-column>
                <el-table-column prop="newWords" label="每日新词" width="120"></el-table-column>
                <el-table-column prop="review" label="每日复习" width="120"></el-table-column>
                <el-table-column prop="period" label="预计周期"></el-table-column>
              </el-table>
            </el-card>

            <el-card class="guide-card">
              <h3>🎉 开始您的单词学习之旅！</h3>
              <el-alert type="success" :closable="false">
                <template slot="title">
                  <div style="font-size: 16px; font-weight: bold;">
                    <i class="el-icon-success"></i> 记住：
                  </div>
                </template>
                <ul style="margin: 10px 0 0 20px;">
                  <li>📚 坚持每日学习</li>
                  <li>🔄 按时复习巩固</li>
                  <li>📈 关注学习报告</li>
                  <li>🏆 挑战更高等级</li>
                </ul>
              </el-alert>
              <div style="text-align: center; margin-top: 20px; font-size: 18px; color: #409EFF;">
                <strong>祝您学习愉快！早日成为单词达人！</strong> 
              </div>
            </el-card>
          </div>
        </el-tab-pane>
      </el-tabs>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Help',
  data() {
    return {
      activeTab: 'quickstart',
      goalTable: [
        {
          stage: '初学者',
          newWords: '10-15',
          review: '20-30',
          period: '3-6个月'
        },
        {
          stage: '进阶者',
          newWords: '20-30',
          review: '40-60',
          period: '2-4个月'
        },
        {
          stage: '高级者',
          newWords: '30-50',
          review: '60-100',
          period: '1-2个月'
        }
      ]
    };
  }
};
</script>

<style scoped>
.help-page {
  min-height: 100vh;
  /* background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); */
  padding: 20px;
}

.help-container {
  max-width: 1200px;
  margin: 0 auto;
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.help-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 40px 30px;
  text-align: center;
}

.help-header h1 {
  margin: 0 0 10px 0;
  font-size: 36px;
  font-weight: 700;
}

.help-header h1 i {
  margin-right: 12px;
}

.help-header .subtitle {
  margin: 0;
  font-size: 18px;
  opacity: 0.95;
}

.help-tabs {
  margin: 0;
  border: none;
  box-shadow: none;
}

.help-tabs >>> .el-tabs__header {
  background: #f5f7fa;
  margin: 0;
}

.help-tabs >>> .el-tabs__item {
  font-size: 15px;
  font-weight: 500;
}

.tab-content {
  padding: 30px;
  min-height: 600px;
}

.tab-content h2 {
  color: #303133;
  margin: 0 0 25px 0;
  font-size: 28px;
  font-weight: 700;
  border-bottom: 3px solid #409EFF;
  padding-bottom: 10px;
}

.tab-content h3 {
  color: #606266;
  margin: 25px 0 15px 0;
  font-size: 22px;
  font-weight: 600;
}

.tab-content h4 {
  color: #909399;
  margin: 15px 0 10px 0;
  font-size: 16px;
  font-weight: 600;
}

.guide-card {
  margin-bottom: 20px;
}

.guide-card h3 {
  margin-top: 0;
  color: #409EFF;
  font-size: 20px;
}

.guide-card ol,
.guide-card ul {
  margin: 10px 0;
  padding-left: 25px;
}

.guide-card li {
  margin: 8px 0;
  line-height: 1.8;
  color: #606266;
}

.el-alert {
  margin-top: 15px;
}

.el-collapse {
  border: none;
}

.el-collapse >>> .el-collapse-item__header {
  font-size: 16px;
  font-weight: 500;
  color: #303133;
  background: #f5f7fa;
  padding-left: 20px;
  border-radius: 8px;
  margin-bottom: 10px;
}

.el-collapse >>> .el-collapse-item__content {
  padding: 15px 20px;
}

.el-steps >>> .el-step__title {
  font-size: 16px;
  font-weight: 600;
}

.el-steps >>> .el-step__description {
  padding-right: 20px;
}

.el-steps >>> .el-step__description h4 {
  margin: 10px 0 8px 0;
  color: #409EFF;
}

.el-steps >>> .el-step__description ol {
  margin: 10px 0;
  padding-left: 20px;
}

.el-steps >>> .el-step__description li {
  margin: 6px 0;
  color: #606266;
}

.el-steps >>> .el-step__description .el-tag {
  margin: 5px 8px 5px 0;
}

.el-steps >>> .el-step__description .el-alert {
  margin: 10px 0;
}

.el-timeline {
  padding-left: 0;
  margin: 15px 0;
}

.tip-item {
  text-align: center;
  padding: 20px 10px;
  margin-bottom: 20px;
  border-radius: 10px;
  background: #f5f7fa;
  transition: all 0.3s;
}

.tip-item:hover {
  background: #ecf5ff;
  transform: translateY(-3px);
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.2);
}

.tip-item i {
  font-size: 36px;
  color: #409EFF;
  display: block;
  margin-bottom: 10px;
}

.tip-item h4 {
  margin: 10px 0 5px 0;
  color: #303133;
  font-size: 16px;
}

.tip-item p {
  margin: 0;
  color: #909399;
  font-size: 13px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .help-page {
    padding: 10px;
  }

  .help-header {
    padding: 30px 20px;
  }

  .help-header h1 {
    font-size: 28px;
  }

  .help-header .subtitle {
    font-size: 16px;
  }

  .tab-content {
    padding: 20px 15px;
  }

  .tab-content h2 {
    font-size: 24px;
  }

  .tab-content h3 {
    font-size: 20px;
  }

  .tip-item {
    margin-bottom: 15px;
  }

  .el-col {
    margin-bottom: 10px;
  }
}

@media (max-width: 600px) {
  .help-tabs >>> .el-tabs__item {
    padding: 0 10px;
    font-size: 13px;
  }

  .tab-content h2 {
    font-size: 20px;
  }

  .tab-content h3 {
    font-size: 18px;
  }
}
</style>
